import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Checkbox, Form, Input, List, Select } from 'antd';
import { DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import { useAsyncFn } from 'react-use';
import { DragDropContext, Draggable, Droppable, OnDragEndResponder } from 'react-beautiful-dnd';
import { debounce } from 'es-toolkit';
import { ProjectResponseModel, Story, StoryType, Theme } from '@uniquegood/realworld-admin-interface';
import Button from '@src/components/Button';
import { noticeApi, projectApi } from '@src/apis/admin';

const PAGE_SIZE = 20;

const storyTypeDefaultValue = StoryType.TextHeader satisfies StoryType;

interface FormFields {
  title: string;
  type: StoryType;
  content?: string;
  backgroundImage?: string;
  theme: Theme;
  projectIds: string[];
  isDisabled: boolean;
}

type ModalContentProps = {
  onClose(): unknown;
  storyId?: string;
  refetchHandler: () => void;
} & ({ type: 'create'; data?: undefined } | { type: 'update'; data: Story });

function reorder<T>(list: Array<T>, startIndex: number, endIndex: number) {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
}

export default function ModalContent({ type, onClose, data, storyId, refetchHandler }: ModalContentProps) {
  const [selectedType, setSelectedType] = useState<StoryType>(data?.type ?? storyTypeDefaultValue);
  const [form] = Form.useForm<FormFields>();

  const [projectIds, setProjectIds] = useState<string[]>(data?.projectIds ?? []);
  // 선택된 프로젝트 정보를 저장 (id -> project 매핑)
  const [selectedProjects, setSelectedProjects] = useState<Map<string, ProjectResponseModel>>(new Map());
  // 검색 결과 프로젝트 목록
  const [searchResults, setSearchResults] = useState<ProjectResponseModel[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  // 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const currentKeywordRef = useRef('');

  const searchProjects = async (keyword: string, page: number = 1, append: boolean = false) => {
    try {
      if (page === 1) {
        setIsSearching(true);
      } else {
        setIsLoadingMore(true);
      }
      const { data } = await projectApi.getProjects(keyword || undefined, undefined, undefined, page, PAGE_SIZE);
      const newResults = data.data.data || [];

      if (append) {
        setSearchResults((prev) => [...prev, ...newResults]);
      } else {
        setSearchResults(newResults);
      }

      // 더 불러올 데이터가 있는지 확인
      setHasMore(newResults.length === PAGE_SIZE);
      setCurrentPage(page);
    } catch (e) {
      console.error(e);
      if (!append) {
        setSearchResults([]);
      }
      setHasMore(false);
    } finally {
      setIsSearching(false);
      setIsLoadingMore(false);
    }
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((keyword: string) => {
        currentKeywordRef.current = keyword;
        setCurrentPage(1);
        setHasMore(true);
        searchProjects(keyword, 1, false);
      }, 300),
    []
  );

  const handlePopupScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    // 스크롤이 바닥에 가까워지면 다음 페이지 로드
    if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 50) {
      if (!isLoadingMore && hasMore) {
        searchProjects(currentKeywordRef.current, currentPage + 1, true);
      }
    }
  }, [isLoadingMore, hasMore, currentPage]);

  // 수정 모드일 때 기존 프로젝트 정보 로드
  useEffect(() => {
    if (data?.projectIds && data.projectIds.length > 0) {
      const existingProjectIds = data.projectIds;
      const loadExistingProjects = async () => {
        const projectMap = new Map<string, ProjectResponseModel>();
        // 각 프로젝트 ID로 개별 조회하여 정보 가져오기
        await Promise.all(
          existingProjectIds.map(async (projectId) => {
            try {
              const { data: projectData } = await projectApi.getProjectsById(projectId);
              if (projectData) {
                // ProjectViewModel을 ProjectResponseModel 형태로 변환
                projectMap.set(projectId, {
                  id: projectData.id,
                  name: projectData.name,
                  description: projectData.description,
                  verticalImageUrl: projectData.imageUrl
                });
              }
            } catch (e) {
              console.error(`Failed to load project ${projectId}`, e);
            }
          })
        );
        setSelectedProjects(projectMap);
      };
      loadExistingProjects();
    }
  }, [data?.projectIds]);

  const [formSubmitState, onFinish] = useAsyncFn(
    async (formData: Omit<FormFields, 'projectIds'>) => {
      const body = { ...formData, projectIds };

      if (type === 'create') {
        await noticeApi.createStory(undefined, body);
      } else {
        await noticeApi.editStory(storyId!, undefined, body);
      }

      onClose();
      refetchHandler();
    },
    [projectIds, type, storyId]
  );

  const onDragEnd: OnDragEndResponder = (result) => {
    if (!result.destination) return;

    setProjectIds((ids) => reorder(ids, result.source.index, result.destination!.index));
  };

  // 검색 결과에서 이미 선택된 프로젝트 제외
  const filteredSearchResults = useMemo(
    () => searchResults.filter((project) => project.id && !projectIds.includes(project.id)),
    [searchResults, projectIds]
  );

  return (
    <Form<FormFields> id="modal" form={form} layout="vertical" onFinish={onFinish}>
      <Form.Item
        label="제목"
        name="title"
        rules={[{ required: true, message: '제목은 필수입니다!' }]}
        initialValue={data?.title}
      >
        <Input placeholder="예) 에이전트X 할인대전" maxLength={30} />
      </Form.Item>
      <Form.Item label="종류" name="type" initialValue={selectedType}>
        <Select<StoryType>
          onSelect={setSelectedType}
          options={[
            {
              value: 0,
              label: 'A타입 (텍스트 타입)'
            },
            {
              value: 1,
              label: 'B타입 (이미지 타입)'
            },
            {
              value: 2,
              label: 'C타입 (웹뷰 타입)'
            }
          ]}
        />
      </Form.Item>
      {selectedType === StoryType.TextHeader && (
        <>
          <Form.Item label="본문 텍스트" name="content" initialValue={data?.content}>
            <Input placeholder="예) 할인 쏙쏙 챙겨가세요" maxLength={80} />
          </Form.Item>
          <Form.Item label="배경" name="backgroundImage" initialValue={data?.backgroundImage}>
            <Input placeholder="예) https://picsum.photos/720/720" maxLength={80} />
          </Form.Item>
          <Form.Item label="테마" name="theme" initialValue={data?.theme}>
            <Select<Theme>
              options={[
                {
                  value: 0,
                  label: '라이트'
                },
                {
                  value: 1,
                  label: '다크'
                }
              ]}
            />
          </Form.Item>
        </>
      )}
      {selectedType === StoryType.ImageHeader && (
        <Form.Item
          label="본문 이미지"
          name="content"
          rules={[{ required: true, message: '본문 이미지는 필수입니다!' }]}
          initialValue={data?.content}
        >
          <Input placeholder="https://picsum.photos/720/720" />
        </Form.Item>
      )}
      <Form.Item label="게임 목록" required>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                <List<string>
                  dataSource={projectIds}
                  header={
                    <Select
                      onSelect={(value) => {
                        if (!value) return;
                        // 선택한 프로젝트 정보 저장
                        const selectedProject = filteredSearchResults.find((p) => p.id === value);
                        if (selectedProject) {
                          setSelectedProjects((prev) => new Map(prev).set(value, selectedProject));
                        }
                        setProjectIds((ids) => (ids.includes(value) ? ids : [...ids, value]));
                      }}
                      filterOption={false}
                      onSearch={debouncedSearch}
                      onPopupScroll={handlePopupScroll}
                      showSearch
                      value={null}
                      loading={isSearching || isLoadingMore}
                      notFoundContent={isSearching ? '검색 중...' : '검색 결과가 없습니다.'}
                      placeholder="프로젝트 이름을 검색하세요"
                    >
                      {filteredSearchResults.map((project) => (
                        <Select.Option key={project.id} value={project.id}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Avatar
                              src={project.verticalImageUrl}
                              size={24}
                              shape="square"
                              style={{ flexShrink: 0 }}
                            >
                              {project.name?.[0]}
                            </Avatar>
                            <span>{project.name}</span>
                          </div>
                        </Select.Option>
                      ))}
                    </Select>
                  }
                  bordered
                  size="small"
                  renderItem={(projectId, index) => (
                    <Draggable draggableId={`draggable-${index}`} index={index}>
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                          <List.Item style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <Avatar
                                src={selectedProjects.get(projectId)?.verticalImageUrl}
                                size={24}
                                shape="square"
                                style={{ flexShrink: 0 }}
                              >
                                {selectedProjects.get(projectId)?.name?.[0]}
                              </Avatar>
                              <span>{selectedProjects.get(projectId)?.name || projectId}</span>
                            </div>
                            <Button
                              size="small"
                              icon={<DeleteOutlined />}
                              onClick={() => {
                                setProjectIds((ids) => ids.filter((id) => id !== projectId));
                              }}
                            />
                          </List.Item>
                        </div>
                      )}
                    </Draggable>
                  )}
                />
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Form.Item>
      <Form.Item name="isDisabled" valuePropName="checked" initialValue={data?.isDisabled}>
        <Checkbox>비활성화</Checkbox>
      </Form.Item>
      <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={formSubmitState.loading}>
        저장
      </Button>
    </Form>
  );
}
